import React, { useEffect } from 'react';
import { ArrowRight, Check, Clock3, LayoutDashboard, Shield, Sparkles } from 'lucide-react';
import { resolveLandingOrigin, resolvePanelLoginUrl, resolvePanelRegisterUrl } from '../utils/booking';
import { applySeo } from '../utils/seo';
import { getIndustryPageBySlug, industryPages } from '../data/industryPages';
import '../styles/landing.css';
import '../styles/industry-pages.css';
import '../styles/conversion-pages.css';

function IndustryFaqItem({ question, answer }) {
  return (
    <article className="industry-faq-item">
      <h3>{question}</h3>
      <p>{answer}</p>
    </article>
  );
}

export default function IndustrySeoPage({ slug }) {
  const page = getIndustryPageBySlug(slug) || industryPages.fryzjer;
  const panelLoginUrl = resolvePanelLoginUrl();
  const panelRegisterUrl = resolvePanelRegisterUrl();
  const featureIcons = {
    sparkles: Sparkles,
    check: Check,
    clock: Clock3,
    dashboard: LayoutDashboard,
    shield: Shield,
  };

  useEffect(() => {
    const landingOrigin = resolveLandingOrigin();
    const pageUrl = `${landingOrigin}${page.path}`;

    applySeo({
      title: page.title,
      description: page.description,
      keywords: page.keywords,
      robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
      canonical: pageUrl,
      ogTitle: page.title,
      ogDescription: page.description,
      ogUrl: pageUrl,
      themeColor: '#f8fafb',
      structuredData: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebPage',
            '@id': `${pageUrl}#webpage`,
            url: pageUrl,
            name: page.title,
            description: page.description,
            inLanguage: 'pl-PL',
            isPartOf: {
              '@id': `${landingOrigin}#website`,
            },
          },
          {
            '@type': 'FAQPage',
            '@id': `${pageUrl}#faq`,
            mainEntity: page.faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          },
        ],
      },
    });
  }, [page]);

  return (
    <div className="landing-page industry-page">
      <div className="conversion-sticky-bar">
        <div className="landing-container conversion-sticky-inner">
          <div className="conversion-sticky-copy">
            <strong>Zmień płatny kalendarz na ClientWay w 2 minuty.</strong>
            <span>0 zł abonamentu, 0 zł prowizji, 0 zł za SMS.</span>
          </div>
          <a href={panelRegisterUrl} className="conversion-sticky-cta">
            Załóż darmowe konto
            <ArrowRight size={14} />
          </a>
        </div>
      </div>

      <nav className="landing-nav industry-nav" aria-label="Nawigacja branżowa">
        <a href="/" className="landing-nav-logo" aria-label="ClientWay - strona główna">
          <span className="landing-logo-icon"><LayoutDashboard size={18} /></span>
          <span className="landing-logo-text">Client<span>Way</span></span>
        </a>

        <div className="industry-nav-links">
          <a href="/">Strona główna</a>
          <a href={panelLoginUrl}>Zaloguj się</a>
          <a href={panelRegisterUrl} className="industry-nav-cta">Zacznij za darmo</a>
        </div>
      </nav>

      <section className="industry-hero">
        <div className="industry-hero-grid" aria-hidden="true" />
        <div className="industry-hero-glow" aria-hidden="true" />
        <div className="landing-container industry-hero-inner">
          <div className="landing-badge">
            <Sparkles size={12} />
            {page.badge}
          </div>
          <h1>{page.h1}</h1>
          <p className="industry-hero-lead">{page.heroLead}</p>
          <div className="industry-hero-actions">
            <a href={panelRegisterUrl} className="landing-btn-hero-primary">
              Rozpocznij za darmo
              <ArrowRight size={16} />
            </a>
            <a href={panelLoginUrl} className="landing-btn-hero-secondary">
              <Shield size={16} />
              Przejdź do panelu
            </a>
          </div>

          <div className="industry-hero-pills" aria-label="Najważniejsze korzyści">
            <span><Check size={14} /> Darmowy system</span>
            <span><Check size={14} /> Rezerwacje 24/7</span>
            <span><Check size={14} /> Przypomnienia SMS</span>
            <span><Check size={14} /> Baza klientów</span>
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-container industry-copy-grid">
          <div className="industry-copy-block">
            <p className="landing-section-label">Dlaczego ClientWay?</p>
            <h2>{page.featuresTitle}</h2>
            <p>{page.featuresSubtitle}</p>
          </div>

          <aside className="industry-side-card">
            <div className="industry-side-card-top">
              <Clock3 size={16} />
              Rezerwacje 24/7
            </div>
            <ul className="industry-command-list">
              <li><Check size={14} /><span>Klienci zapisują się wtedy, kiedy mają czas.</span></li>
              <li><Check size={14} /><span>Ty nie odbierasz telefonu w trakcie pracy.</span></li>
              <li><Check size={14} /><span>Wszystko widzisz w jednym panelu.</span></li>
              <li><Check size={14} /><span>System działa 24/7, także po godzinach.</span></li>
            </ul>
          </aside>
        </div>
      </section>

      <section className="landing-section landing-section-muted">
        <div className="landing-container">
          <div className="industry-feature-grid">
            {page.featureCards.map((item) => {
              const Icon = featureIcons[item.icon] || Check;

              return (
                <article key={item.title} className="industry-content-card">
                  <div className="industry-feature-icon">
                    <Icon size={18} />
                  </div>
                  <h2>{item.title}</h2>
                  <p>{item.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-container industry-text-wrap">
          <div className="industry-text-copy">
            <p className="landing-section-label">Twoje Centrum Dowodzenia</p>
            <h2>{page.commandCenterTitle}</h2>
            <p>{page.commandCenterSubtitle}</p>
            <p>{page.intro}</p>
          </div>
          <div className="industry-search-card">
            <div className="industry-search-card-icon"><LayoutDashboard size={18} /></div>
            <h3>{page.commandCenterSubtitle}</h3>
            <ul className="industry-command-list">
              {page.commandCenter.map((point) => (
                <li key={point}>
                  <Check size={14} />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="landing-section landing-section-muted" aria-labelledby="faq-heading">
        <div className="landing-container">
          <div className="landing-section-header">
            <p className="landing-section-label">Techniczna Uczciwość</p>
            <h2 id="faq-heading">{page.trustTitle}</h2>
            <p>{page.trustSubtitle}</p>
          </div>

          <div className="industry-trust-grid">
            <article className="industry-faq-item industry-trust-card">
              <h3>Dlaczego nam ufać?</h3>
              <p>
                W naszym kodzie nie ma modułów płatniczych, więc nie mamy jak ściągnąć od Ciebie pieniędzy.
                Twoje dane są chronione, hasła są hashowane, a system jest tak lekki, że działa płynnie nawet przy słabym internecie w salonie.
              </p>
              <ul className="industry-command-list">
                {page.trust.map((point) => (
                  <li key={point}>
                    <Check size={14} />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </article>

            {page.faqs.map((faq) => (
              <IndustryFaqItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-container industry-related-wrap">
          <div className="industry-related-copy">
            <p className="landing-section-label">Własna strona rezerwacji</p>
            <h2>Własna strona rezerwacji w 5 minut</h2>
            <p>Udostępnij link do zapisów w social mediach, na stronie lub w mapach i zacznij zbierać rezerwacje od razu.</p>
          </div>

          <div className="industry-related-links">
            {page.related.map((relatedSlug) => {
              const relatedPage = industryPages[relatedSlug];
              if (!relatedPage) {
                return null;
              }

              return (
                <a key={relatedPage.slug} href={relatedPage.path} className="industry-related-link">
                  {relatedPage.badge}
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-container">
          <article className="industry-transfer-box">
            <div>
              <p className="landing-section-label">Zmień system w 2 minuty</p>
              <h2>{page.migrationBox?.title || 'Zmień system w 2 minuty'}</h2>
              <p>{page.migrationBox?.text || 'Używasz innego systemu? Pomożemy Ci zaimportować bazę klientów za darmo. Nie trać więcej pieniędzy na prowizje.'}</p>
            </div>
            <a href={panelRegisterUrl} className="landing-btn-cta-primary">
              {page.migrationBox?.cta || 'Załóż darmowe konto'}
              <ArrowRight size={16} />
            </a>
          </article>
        </div>
      </section>

      <section className="landing-final-cta industry-final-cta" aria-labelledby="industry-cta">
        <div className="landing-container landing-cta-inner">
          <div className="landing-badge landing-badge-cta">
            <Shield size={12} />
            Darmowe dla usługodawców · Bez zobowiązań
          </div>
          <h2 id="industry-cta">{page.ctaTitle}</h2>
          <p>{page.ctaSubtitle}</p>
          <div className="landing-cta-buttons">
            <a href={panelRegisterUrl} className="landing-btn-cta-primary">
              {page.ctaButtonLabel}
              <ArrowRight size={16} />
            </a>
            <a href={panelLoginUrl} className="landing-btn-cta-secondary">
              Zaloguj się
            </a>
          </div>
          <p className="industry-cta-note">{page.ctaNote}</p>
        </div>
      </section>
    </div>
  );
}
